/**
 * Complete a next event — and, in the same step, book what it was leading to
 * and set what happens after.
 *
 * The showroom flow is "call the customer → they say yes → book it". Splitting
 * that across three screens is how a lead ends up with a completed call and no
 * appointment. The server takes all three in one transaction
 * (POST /v1/tasks/{id}/complete), so a failure leaves nothing half-done.
 *
 * "Set the next step" defaults ON for any lead that is still live. Every open
 * lead is supposed to have a next action; asking for the next one at the moment
 * the last is closed is the cheapest way to hold that invariant.
 */

import { useEffect, useMemo, useState } from 'react';
import { tasksService } from '../lib/services';
import { useToast } from '../lib/toast';
import SlotPicker from './SlotPicker';
import { APPT_TYPES } from '../lib/appointmentMeta';
import {
  orgWallClockToISO,
  orgTimezoneAbbrev,
  civilToISODate,
  orgCivilToday,
  orgAddDays,
} from '../lib/datetime';

const OUTCOMES = [
  { value: 'CONTACTED',      label: 'Contacted',      positive: true },
  { value: 'NO_ANSWER',      label: 'No answer',      positive: false },
  { value: 'LEFT_MESSAGE',   label: 'Left message',   positive: false },
  { value: 'RESCHEDULED',    label: 'Rescheduled',    positive: false },
  { value: 'NOT_INTERESTED', label: 'Not interested', positive: false },
];

/** Follow-up defaults by outcome — what a consultant would pick anyway. */
const FOLLOW_UP_DEFAULT = {
  CONTACTED:      { days: 3, title: 'Follow up' },
  NO_ANSWER:      { days: 1, title: 'Try again' },
  LEFT_MESSAGE:   { days: 1, title: 'Chase the message' },
  RESCHEDULED:    { days: 2, title: 'Confirm the new time' },
  NOT_INTERESTED: null,
};

function defaultFollowUpLocal(days) {
  const civil = orgAddDays(orgCivilToday(), days);
  return `${civilToISODate(civil)}T09:00`;
}

/**
 * @param open
 * @param task          the task being completed (required)
 * @param lead          optional — enables the booking step and gives it context
 * @param convertsToAppointment  from the task's reason
 * @param onClose
 * @param onCompleted   (result) => void — result is { task, appointment?, nextTask? }
 */
export default function CompleteTaskModal({
  open,
  task,
  lead = null,
  convertsToAppointment = false,
  onClose,
  onCompleted,
}) {
  const { toast } = useToast();

  const [outcome, setOutcome] = useState('CONTACTED');
  const [book, setBook] = useState(false);
  const [apptType, setApptType] = useState('TEST_DRIVE');
  const [apptDate, setApptDate] = useState(() => civilToISODate(orgCivilToday()));
  const [slot, setSlot] = useState(null);
  const [location, setLocation] = useState('');
  const [apptNotes, setApptNotes] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [notifyAssignee, setNotifyAssignee] = useState(true);
  const [setNext, setSetNext] = useState(true);
  const [nextTitle, setNextTitle] = useState('Follow up');
  const [nextDueLocal, setNextDueLocal] = useState(() => defaultFollowUpLocal(3));
  const [busy, setBusy] = useState(false);

  const leadClosed = lead?.stage === 'DELIVERED' || lead?.stage === 'LOST';

  useEffect(() => {
    if (!open) return;
    setOutcome('CONTACTED');
    setBook(convertsToAppointment);
    setApptType('TEST_DRIVE');
    setApptDate(civilToISODate(orgCivilToday()));
    setSlot(null);
    setLocation('');
    setApptNotes('');
    setNotifyCustomer(true);
    setNotifyAssignee(true);
    setSetNext(!leadClosed);
    setNextTitle('Follow up');
    setNextDueLocal(defaultFollowUpLocal(3));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outcome drives what the next step should be — a no-answer is chased
  // tomorrow, a good conversation in a few days.
  useEffect(() => {
    const d = FOLLOW_UP_DEFAULT[outcome];
    if (!d) { setSetNext(false); return; }
    setNextTitle(d.title);
    setNextDueLocal(defaultFollowUpLocal(d.days));
    if (!leadClosed) setSetNext(true);
    if (outcome !== 'CONTACTED') setBook(false);
  }, [outcome, leadClosed]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const canBook = outcome === 'CONTACTED' && !!lead;
  const bookingReady = !book || !!slot;
  const nextReady = !setNext || (nextTitle.trim() && nextDueLocal);
  const canSubmit = !busy && bookingReady && nextReady;

  const typeOptions = useMemo(() => APPT_TYPES.filter((t) => t.stream === 'SALES'), []);

  if (!open || !task) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const body = { outcome };

      if (book && slot) {
        body.createAppointment = {
          type: apptType,
          startsAt: slot.startsAt,
          endsAt: slot.endsAt,
          consultantId: slot.consultantId,
          leadId: lead?.id || task.leadId || null,
          customerId: lead?.customerId || task.customerId || null,
          vehicleId: lead?.vehicleOfInterestId || null,
          location: location.trim() || null,
          notes: apptNotes.trim() || null,
          notifyCustomer,
          notifyAssignee,
        };
      }

      if (setNext) {
        body.nextTask = {
          title: nextTitle.trim(),
          dueAt: orgWallClockToISO(nextDueLocal),
          assigneeId: task.assigneeId || null,
          leadId: task.leadId || null,
          customerId: task.customerId || null,
        };
      }

      const result = await tasksService.complete(task.id, body);
      toast(
        book ? 'Completed and booked' : (setNext ? 'Completed — next step set' : 'Completed'),
        { variant: 'success' },
      );
      onCompleted?.(result);
      onClose?.();
    } catch (err) {
      toast(`Could not complete: ${err?.response?.data?.message || err?.message || 'unknown error'}`,
        { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form
        className="modal"
        style={{ width: 'min(620px, 94vw)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="modal-header">
          <div className="modal-title">Complete: {task.title}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-field">
            <label>How did it go?</label>
            <div className="flex-row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {OUTCOMES.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`pill${outcome === o.value ? ' active' : ''}`}
                  onClick={() => setOutcome(o.value)}
                  aria-pressed={outcome === o.value}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {canBook && (
            <div className="card" style={{ padding: 12 }}>
              <label className="flex-row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={book} onChange={(e) => setBook(e.target.checked)} />
                <span style={{ fontWeight: 600 }}>Book the appointment now</span>
              </label>

              {book && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="form-field">
                    <label htmlFor="ct-type">Type</label>
                    <select id="ct-type" value={apptType} onChange={(e) => { setApptType(e.target.value); setSlot(null); }}>
                      {typeOptions.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <SlotPicker
                    date={apptDate}
                    onDateChange={(d) => { setApptDate(d); setSlot(null); }}
                    type={apptType}
                    value={slot}
                    onSelect={setSlot}
                    disabled={busy}
                  />

                  <div className="form-field">
                    <label htmlFor="ct-location">Location</label>
                    <input
                      id="ct-location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Showroom floor, customer's address…"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="ct-notes">Note</label>
                    <textarea
                      id="ct-notes"
                      rows={2}
                      value={apptNotes}
                      onChange={(e) => setApptNotes(e.target.value)}
                      placeholder="Test drive MG ZS, has listed trade"
                    />
                  </div>

                  <div className="flex-row" style={{ gap: 16, flexWrap: 'wrap' }}>
                    <label className="flex-row" style={{ alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifyCustomer}
                        onChange={(e) => setNotifyCustomer(e.target.checked)}
                      />
                      <span>Notify customer</span>
                    </label>
                    <label className="flex-row" style={{ alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifyAssignee}
                        onChange={(e) => setNotifyAssignee(e.target.checked)}
                      />
                      <span>Notify assignee</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card" style={{ padding: 12 }}>
            <label className="flex-row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={setNext} onChange={(e) => setSetNext(e.target.checked)} />
              <span style={{ fontWeight: 600 }}>Set the next step</span>
            </label>

            {setNext ? (
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12 }}>
                <div className="form-field">
                  <label htmlFor="ct-next-title">What next</label>
                  <input
                    id="ct-next-title"
                    value={nextTitle}
                    onChange={(e) => setNextTitle(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="ct-next-due">When <span className="text-muted">({orgTimezoneAbbrev()})</span></label>
                  <input
                    id="ct-next-due"
                    type="datetime-local"
                    value={nextDueLocal}
                    onChange={(e) => setNextDueLocal(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              !leadClosed && (
                <div className="mono text-xs" style={{ marginTop: 8, color: 'var(--warning)' }}>
                  This lead will have no next step.
                </div>
              )
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Complete'}
          </button>
        </div>
      </form>
    </div>
  );
}
